import React, {
  ReactElement,
  useEffect,
  useState,
  useCallback,
  useReducer,
} from 'react';
import { Entity } from '../../types/entities/Entity';
import {
  Divider,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableContainer,
  Typography,
} from '@mui/material';
import api from '../../services/api';
import Box from '@mui/material/Box';
import { debounce } from 'lodash';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { ServiceReservationFinanceRow } from './ServiceReservationFinanceRow';
import { SPNumberField } from '../Table/fields/SPNumberField';
import { formatNumber } from '../../reducer/actions/formatNumber';
import RefreshIcon from '@mui/icons-material/Refresh';

type Props<T extends Entity> = {
  values: any;
  allValues: any;
  schema: any;
  onChange: (value: any, column: string) => void;
};

const reducer = (state: any, action: any) => {
  switch (action.type) {
    case 'SET_INPUT':
      const newCurrent: any = { ...state.current };
      const newHistory: any = { ...state.history };

      if (action.payload.type === 'group_size') {
        newHistory.group_size = action.payload.value;
        newCurrent.group_size = action.payload.value;
      } else {
        const finances = [...newCurrent.finances];
        const newFinances = finances.map((finance: any) => {
          if (finance?.id === action.payload.id) {
            finance[`${action.payload.column}`] = action.payload.value;
          }
          return finance;
        });

        newHistory.finances = newFinances;
        newCurrent.finances = newFinances;
      }

      return {
        ...state,
        current: newCurrent,
        history: newHistory,
        send: true,
        lastChangedColumn: action.payload.type,
      };
    case 'SET_HISTORY':
      const tempAction: any = { ...action };

      return {
        ...state,
        history: {},
        current: tempAction.payload,
        send: false,
      };
    default:
      return state;
  }
};

export const ServiceReservationFinance = <T extends Entity>({
  values,
  allValues,
  schema,
  onChange,
}: Props<T>): ReactElement => {
  const [loading, setLoading] = useState<boolean>(false);
  const [eurCurrentValue, setEurCurrentValue] = useState<any>(undefined);

  const [state, dispatch] = useReducer(reducer, {
    history: [],
    current: {
      group_size: values?.activity_reservation?.group_size,
      finances: [],
    },
    send: false,
  });

  const fetchData = useCallback(() => {
    setLoading(true);
    const searchParams = new URLSearchParams({});
    api
      .get(`/service-reservation-finances/${values.id}`, searchParams)
      .then((res: any) => {
        dispatch({
          type: 'SET_HISTORY',
          payload: {
            finances: res,
            group_size: values?.size,
          },
        });
        setLoading(false);
      });
  }, [values]);
  const fetchExchangeRate = useCallback(async () => {
    const response = await fetch(
      `https://api.exchangerate-api.com/v4/latest/EUR`
    );
    const data = await response.json();
    setEurCurrentValue(data.rates.HUF);
  }, []);
  const handleRefreshFinance = () => {
    setLoading(true);
    api
      .post(`/service-reservations/update-finance/${values.id}`, values)
      .then((res: any) => {
        fetchData();
        setLoading(false);
      });
  };
  const handleSaveData = (column: any, value: any, type: any, id?: any) => {
    dispatch({
      type: 'SET_INPUT',
      payload: {
        type: type,
        column: column,
        value: value,
        id: id,
      },
    });
  };

  const debouncedChange = useCallback(
    debounce(() => {
      if (state.send) {
        if (state.lastChangedColumn === 'group_size') {
          onChange(state.lastChangedColumn, state.history['group_size']);
        } else {
          onChange(state.lastChangedColumn, state.history['finances']);
        }
      }
    }, 500),
    [state]
  );

  const [sum, setSum] = useState<any>({
    sum: 0,
    sum_eur: 0,
    pp_sum: 0,
    pp_sum_eur: 0,
  });

  useEffect(() => {
    debouncedChange();
  }, [state, debouncedChange]);

  useEffect(() => {
    fetchData();
  }, [schema, values]);

  useEffect(() => {
    fetchExchangeRate();
  }, []);

  useEffect(() => {
    if (eurCurrentValue) {
      let sum = 0;
      let sumEur = 0;

      console.log(state.current);

      state.current.finances.map((finance: any) => {
        if (finance?.currency?.id === 2) {
          sum += finance.gross_cost;
        }
        if (finance?.currency?.id === 1) {
          sumEur += finance.gross_cost;
        }
      });

      sumEur = parseFloat((sumEur * eurCurrentValue).toFixed(2));

      setSum({
        sum: sum + sumEur,
        sum_eur: parseFloat(((sum + sumEur) / eurCurrentValue).toFixed(2)),

        pp_sum: parseInt(
          ((sum + sumEur) / state.current.group_size).toFixed(0)
        ),
        pp_sum_eur: parseInt(
          ((sum + sumEur) / state.current.group_size / eurCurrentValue).toFixed(
            0
          )
        ),
      });
    }
  }, [state, eurCurrentValue]);

  return (
    <>
      {loading ? (
        <>
          <Skeleton
            sx={{
              mt: '10px',
            }}
            variant="rounded"
            width={'100%'}
            height={50}
          />
          <Skeleton
            sx={{
              mt: '10px',
            }}
            variant="rounded"
            width={'100%'}
            height={50}
          />
          <Skeleton
            sx={{
              mt: '10px',
            }}
            variant="rounded"
            width={'100%'}
            height={200}
          />
        </>
      ) : (
        <>
          <Stack
            direction={'row'}
            alignItems={'center'}
            justifyContent={'space-between'}
            sx={{
              marginBottom: '30px',
            }}
          >
            <Stack direction={'row'} alignItems={'center'}>
              <Typography variant={'h6'} marginRight={'10px'}>
                GROUP SIZE
              </Typography>
              <Box
                sx={{
                  width: '100px',
                  '& .MuiInputBase-root.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                  },
                }}
              >
                <SPNumberField
                  defaultValue={values?.size}
                  isActive={true}
                  onChange={(value) => {
                    handleSaveData('group_size', value, 'group_size');
                  }}
                />
              </Box>
            </Stack>

            <IconButton onClick={() => handleRefreshFinance()}>
              <RefreshIcon color={'primary'} />
            </IconButton>
          </Stack>

          <TableContainer component={Paper}>
            <Table
              aria-label="simple table"
              size="small"
              sx={{
                '& .MuiTableBody-root .MuiTableRow-root': {
                  background: '#fff',
                },
                '& .MuiTableCell-root': {
                  padding: '2px',
                },
                '& .MuiInputBase-root.MuiOutlinedInput-root': {
                  paddingRight: '0px',
                },
              }}
            >
              <TableHead
                sx={{
                  '& .MuiTableCell-root': {
                    color: 'inherit',
                    border: 'none',
                  },
                  '& .MuiTableRow-root .MuiTableCell-root.MuiTableCell-head': {
                    border: 'none',
                    fontSize: '12px',
                    p: '5px',
                  },
                  background: '#256861',
                  color: '#fff',
                }}
              >
                <TableRow
                  sx={{
                    '& .MuiTableCell-root': {
                      overflowWrap: 'anywhere',
                      padding: '2px',
                    },
                  }}
                >
                  <TableCell>Item</TableCell>
                  <TableCell>Paid By</TableCell>
                  <TableCell align={'right'}>Gross Cost</TableCell>
                  <TableCell>Currency</TableCell>
                  <TableCell align={'right'}>VAT %</TableCell>
                  <TableCell align={'right'}>IFA %</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {state.current.finances.map((finance: any) => {
                  return (
                    <ServiceReservationFinanceRow
                      values={finance}
                      onChange={(value, column) => {
                        console.log(value, column);

                        handleSaveData(column, value, 'finances', finance.id);
                      }}
                    />
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Stack direction={'row'} mt={'30px'}>
            <Stack width={'50%'}></Stack>
            <Stack width={'50%'} direction={'column'}>
              <Stack direction={'row'} alignItems={'center'}>
                <Box
                  sx={{
                    width: '30px',
                  }}
                >
                  <b>∑</b>
                </Box>
                <Stack direction={'column'} width={'100%'}>
                  <Typography variant={'subtitle1'} fontWeight={'bold'}>
                    {`${formatNumber(sum.sum)} HUF`}
                  </Typography>
                  <Typography variant={'subtitle1'} fontWeight={'bold'}>
                    {`${formatNumber(sum.sum_eur)} EUR`}
                  </Typography>
                  <Divider />
                </Stack>
              </Stack>

              <Stack direction={'row'} alignItems={'center'}>
                <Box
                  sx={{
                    width: '30px',
                  }}
                >
                  <b>PP</b>
                </Box>
                <Stack direction={'column'} width={'100%'}>
                  <Typography variant={'subtitle1'} fontWeight={'bold'}>
                    {`${formatNumber(sum.pp_sum)} HUF`}
                  </Typography>
                  <Typography variant={'subtitle1'} fontWeight={'bold'}>
                    {`${formatNumber(sum.pp_sum_eur)} EUR`}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </>
      )}
    </>
  );
};
